/**
 * Enterprise Systems API Service
 *
 * Handles all API calls related to Enterprise Systems Module (Phases 1-3):
 * - System CRUD operations
 * - Research job management
 * - Quality assessments
 * - Module, schema, and API data retrieval
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://graph-solution-backend.huhoka.com/api';

// Create dedicated axios instance for enterprise systems
const enterpriseSystemsClient = axios.create({
  baseURL: `${API_BASE_URL}/enterprise-systems`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
enterpriseSystemsClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for research endpoints (AI-powered)
    if (config.url?.includes('/research')) {
      config.timeout = 180000; // 3 minutes for AI research
    }

    // Increase timeout for quality assessment (can be computation-heavy)
    if (config.url?.includes('/quality')) {
      config.timeout = 120000; // 2 minutes for quality assessment
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface EnterpriseSystem {
  system_id: string;
  system_name: string;
  vendor: string;
  category: string;
  custom_category_name?: string;
  version?: string;
  database_type?: string;
  product_url?: string;
  api_types: string[];
  auth_methods: string[];
  status: string;
  quality_score: number;
  research_version: number;
  last_researched?: string;
  current_phase?: string;
  total_modules: number;
  total_tables: number;
  total_apis: number;
  created_at: string;
  updated_at: string;
}

export interface EnterpriseSystemCreate {
  system_name: string;
  vendor: string;
  category: string;
  custom_category_name?: string;
  version?: string;
  database_type?: string;
  product_url?: string;
  trigger_research?: boolean;
}

export interface EnterpriseSystemUpdate {
  system_name?: string;
  vendor?: string;
  category?: string;
  custom_category_name?: string;
  version?: string;
  database_type?: string;
  product_url?: string;
  api_types?: string[];
  auth_methods?: string[];
}

export interface SystemModule {
  module_id: string;
  system_id: string;
  name: string;
  category?: string;
  description?: string;
  purpose?: string;
  key_functions: string[];
  integration_points: string[];
  typical_users: string[];
  created_at: string;
}

export interface SchemaTable {
  table_id: string;
  system_id: string;
  module_id?: string;
  table_name: string;
  description?: string;
  purpose?: string;
  record_count_estimate?: number;
  business_context?: string;
  primary_keys: string[];
  total_columns: number;
  created_at: string;
}

export interface TableColumn {
  column_id: string;
  table_id: string;
  column_name: string;
  data_type: string;
  max_length?: number;
  is_nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  default_value?: string;
  description?: string;
  business_meaning?: string;
  example_values: string[];
  created_at: string;
}

export interface APIEndpoint {
  api_id: string;
  system_id: string;
  module_id?: string;
  endpoint_path: string;
  http_method: string;
  description?: string;
  purpose?: string;
  authentication_required: boolean;
  request_format?: string;
  response_format?: string;
  parameters: Array<{
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }>;
  example_request?: Record<string, any>;
  example_response?: Record<string, any>;
  created_at: string;
}

export interface IntegrationPattern {
  pattern_id: string;
  system_id: string;
  pattern_name: string;
  pattern_type: string;
  description?: string;
  use_cases: string[];
  implementation_notes?: string;
  created_at: string;
}

export interface DocumentationLink {
  link_id: string;
  system_id: string;
  link_type: string;
  title: string;
  url: string;
  description?: string;
  language: string;
  is_active: boolean;
  access_level: 'public' | 'licensed' | 'internal';
  created_at: string;
  updated_at: string;
  version?: string;
  last_verified?: string;
}

// Note: Primary ResearchJob interface is defined near line 629 with ResearchPhaseProgress
// This is a simplified version for backward compatibility
export interface ResearchJobLegacy {
  job_id: string;
  system_id?: string;
  status: string;
  current_phase?: string;
  overall_progress: number;
  phase_progress: Array<{
    phase: string;
    status: string;
    progress: number;
    progress_percentage?: number;  // Added for compatibility
    items_found?: number;  // Added for compatibility
    started_at?: string;
    completed_at?: string;
    error_message?: string;
  }>;
  modules_found: number;
  tables_found: number;
  apis_found: number;
  documentation_links_found: number;
  quality_score?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface QualityAssessment {
  assessment_id: string;
  system_id: string;
  system_name: string;
  version: number;
  overall_score: number;
  overall_grade: string;
  dimension_scores: Array<{
    dimension: string;
    score: number;
    weight: number;
    max_possible: number;
    actual_count: number;
    details?: string;
    issues_found: string[];
    recommendations: string[];
  }>;
  critical_issues: number;
  high_issues: number;
  medium_issues: number;
  low_issues: number;
  total_issues: number;
  issues: Array<{
    issue_id: string;
    dimension: string;
    severity: string;
    title: string;
    description: string;
    affected_items: string[];
    recommendation?: string;
    created_at: string;
  }>;
  priority_recommendations: string[];
  status: string;
  is_active: boolean;
  created_at: string;
  completed_at?: string;
  previous_version?: number;
  score_change?: number;
  improvement_areas: string[];
  regression_areas: string[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Encode systemId for use in URL paths
 * Handles special characters like spaces in system IDs
 */
const encodeSystemId = (systemId: string): string => encodeURIComponent(systemId);

// ============================================================================
// Enterprise System CRUD
// ============================================================================

/**
 * Get all enterprise systems with optional filtering
 */
export const getEnterpriseSystems = async (params?: {
  category?: string;
  vendor?: string;
  status?: string;
  min_quality_score?: number;
  page?: number;
  page_size?: number;
}) => {
  const response = await enterpriseSystemsClient.get<{
    systems: EnterpriseSystem[];
    total: number;
    page: number;
    page_size: number;
    // Path is '' (not '/') so the request hits the collection URL without a
    // trailing slash — a trailing '/' makes the backend 307-redirect, doubling
    // the network round-trip for every list call.
  }>('', { params });
  return response.data;
};

/**
 * Get single enterprise system by ID
 */
export const getEnterpriseSystem = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<{
    system: EnterpriseSystem;
    statistics: {
      system_id: string;
      modules: number;
      tables: number;
      columns: number;
      api_endpoints: number;
      documentation_links: number;
      integration_patterns: number;
    };
  }>(`/${encodeSystemId(systemId)}`);
  return response.data;
};

/**
 * Create new enterprise system
 */
export const createEnterpriseSystem = async (data: EnterpriseSystemCreate) => {
  const response = await enterpriseSystemsClient.post<{
    system: EnterpriseSystem;
    message: string;
    research_triggered: boolean;
    research_job_id?: string;
    // Path '' (not '/') to avoid the trailing-slash 307 redirect (see getEnterpriseSystems).
  }>('', data);
  return response.data;
};

/**
 * Update enterprise system
 */
export const updateEnterpriseSystem = async (
  systemId: string,
  data: EnterpriseSystemUpdate
) => {
  const response = await enterpriseSystemsClient.patch<{
    system: EnterpriseSystem;
    message: string;
  }>(`/${encodeSystemId(systemId)}`, data);
  return response.data;
};

/**
 * Delete enterprise system
 */
export const deleteEnterpriseSystem = async (systemId: string) => {
  const response = await enterpriseSystemsClient.delete<{ message: string }>(
    `/${encodeSystemId(systemId)}`
  );
  return response.data;
};

// ============================================================================
// Modules
// ============================================================================

/**
 * Get all modules for a system
 */
export const getSystemModules = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<{
    modules: SystemModule[];
    total: number;
  }>(`/${encodeSystemId(systemId)}/modules`);
  return response.data.modules;
};

/**
 * Get single module details
 */
export const getModule = async (systemId: string, moduleId: string) => {
  const response = await enterpriseSystemsClient.get<{
    module: SystemModule;
    tables_count: number;
    apis_count: number;
  }>(`/${encodeSystemId(systemId)}/modules/${encodeURIComponent(moduleId)}`);
  return response.data;
};

export interface ModuleStats {
  module_code: string;
  module_name: string;
  description?: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category?: string;
  status: 'completed' | 'in_progress' | 'pending';
  tables_count: number;
  columns_count: number;
  apis_count: number;
  integration_patterns_count: number;
  started_at?: string;
  completed_at?: string;
}

export interface ModuleStatsResponse {
  modules: ModuleStats[];
  total_modules: number;
  completed_modules: number;
  overall_stats: {
    total_tables: number;
    total_columns: number;
    total_apis: number;
    total_patterns: number;
  };
}

/**
 * Get per-module statistics with completion status
 */
export const getModuleStats = async (systemId: string): Promise<ModuleStatsResponse> => {
  const response = await enterpriseSystemsClient.get<ModuleStatsResponse>(
    `/${encodeSystemId(systemId)}/modules/stats`
  );
  return response.data;
};

// ============================================================================
// Schema (Tables & Columns)
// ============================================================================

/**
 * Get all tables for a system
 */
export const getSystemTables = async (systemId: string, moduleId?: string) => {
  const params = moduleId ? { module_id: moduleId } : undefined;
  const response = await enterpriseSystemsClient.get<{
    tables: SchemaTable[];
    total: number;
  }>(`/${encodeSystemId(systemId)}/tables`, { params });
  return response.data.tables;
};

/**
 * Get single table with columns
 */
export const getTable = async (systemId: string, tableId: string) => {
  const response = await enterpriseSystemsClient.get<{
    table: SchemaTable;
    columns: TableColumn[];
  }>(`/${encodeSystemId(systemId)}/tables/${encodeURIComponent(tableId)}`);
  return response.data;
};

// ============================================================================
// APIs
// ============================================================================

/**
 * Get all APIs for a system
 */
export const getSystemAPIs = async (systemId: string, moduleId?: string) => {
  const params = moduleId ? { module_id: moduleId } : undefined;
  const response = await enterpriseSystemsClient.get<{
    endpoints: APIEndpoint[];
    total: number;
  }>(`/${encodeSystemId(systemId)}/apis`, { params });
  return response.data.endpoints;  // Backend returns "endpoints" not "apis"
};

/**
 * Get single API details
 */
export const getAPI = async (systemId: string, apiId: string) => {
  const response = await enterpriseSystemsClient.get<{ api: APIEndpoint }>(
    `/${encodeSystemId(systemId)}/apis/${encodeURIComponent(apiId)}`
  );
  return response.data;
};

// ============================================================================
// Integration Patterns
// ============================================================================

/**
 * Get all integration patterns for a system
 */
export const getIntegrationPatterns = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<{
    patterns: IntegrationPattern[];
    total: number;
  }>(`/${encodeSystemId(systemId)}/integrations`);
  return response.data.patterns;
};

/**
 * Trigger standalone integration pattern discovery
 */
export const discoverIntegrationPatterns = async (systemId: string) => {
  const response = await enterpriseSystemsClient.post<{
    job_id: string;
    system_id: string;
    status: string;
    message: string;
    estimated_time_minutes: number;
  }>(`/${encodeSystemId(systemId)}/integrations/discover`);
  return response.data;
};

// ============================================================================
// Documentation
// ============================================================================

/**
 * Get all documentation links for a system
 */
export const getDocumentationLinks = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<{
    links: DocumentationLink[];
    total: number;
  }>(`/${encodeSystemId(systemId)}/documentation`);
  return response.data.links;
};

// ============================================================================
// Quality Assessment
// ============================================================================

/**
 * Trigger quality assessment for a system
 */
export const triggerQualityAssessment = async (
  systemId: string,
  force_reassess = false
) => {
  const response = await enterpriseSystemsClient.post<{
    assessment: QualityAssessment;
    message: string;
  }>(`/${encodeSystemId(systemId)}/quality/assess`, { force_reassess });
  return response.data;
};

/**
 * Get latest quality assessment
 */
export const getLatestQualityAssessment = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<QualityAssessment>(
    `/${encodeSystemId(systemId)}/quality/latest`
  );
  return response.data;
};

/**
 * Get quality assessment history
 */
export const getQualityAssessmentHistory = async (
  systemId: string,
  page = 1,
  pageSize = 10
) => {
  const response = await enterpriseSystemsClient.get<{
    assessments: QualityAssessment[];
    total: number;
    page: number;
    page_size: number;
  }>(`/${encodeSystemId(systemId)}/quality/history`, {
    params: { page, page_size: pageSize },
  });
  return response.data;
};

// ============================================================================
// Statistics
// ============================================================================

/**
 * Get system statistics
 */
export const getSystemStatistics = async (systemId: string) => {
  const response = await enterpriseSystemsClient.get<{
    system_id: string;
    modules: number;
    tables: number;
    columns: number;
    apis: number;
    integration_patterns: number;
    documentation_links: number;
    relationships: number;
    quality_score?: number;
    quality_grade?: string;
    last_researched?: string;
    last_assessed?: string;
  }>(`/${encodeSystemId(systemId)}/statistics`);
  return response.data;
};

/**
 * Schema column interface for data mapping
 */
export interface SchemaColumn {
  column_id: string;
  column_name: string;
  data_type: string;
  description: string;
  is_primary_key: boolean;
  is_foreign_key: boolean;
}

/**
 * Schema table interface for data mapping
 */
export interface DataMappingTable {
  table_id: string;
  table_name: string;
  display_name: string;
  description: string;
  table_type: string;
  module_name: string;
  module_code: string;
  columns: SchemaColumn[];
  column_count: number;
}

/**
 * Schema overview response for data mapping
 */
export interface SchemaOverviewResponse {
  system_id: string;
  system_name: string;
  vendor: string;
  category: string;
  total_tables: number;
  tables: DataMappingTable[];
}

/**
 * Get system schema overview for data mapping
 * Returns tables with their columns in a structure optimized for mapping UI
 */
export const getSystemSchemaOverview = async (
  systemId: string,
  limit: number = 50
): Promise<SchemaOverviewResponse> => {
  const response = await enterpriseSystemsClient.get<SchemaOverviewResponse>(
    `/${encodeSystemId(systemId)}/schema-overview`,
    { params: { limit } }
  );
  return response.data;
};

// ============================================================================
// Research Jobs
// ============================================================================

export interface ResearchPhaseProgress {
  phase: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress_percentage: number;
  items_found: number;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface ResearchJob {
  job_id: string;
  system_id?: string;
  system_name: string;
  vendor: string;
  category: string;
  version?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  current_phase?: string | null;  // Can be null when pending
  overall_progress: number;
  phase_progress?: ResearchPhaseProgress[];  // Can be empty when pending
  results?: {
    modules_found: number;
    tables_found: number;
    columns_found: number;
    apis_found: number;
    integration_patterns_found: number;
    documentation_links_found: number;
  };
  started_at?: string | null;  // Can be null when pending
  completed_at?: string;
  error_message?: string;
}

/**
 * Get research jobs for a system
 */
export const getResearchJobs = async (systemId: string): Promise<ResearchJob[]> => {
  const response = await enterpriseSystemsClient.get<{ jobs: ResearchJob[] }>(
    `/research/jobs`,
    {
      params: { system_id: systemId },
    }
  );
  return response.data.jobs;
};

/**
 * Get specific research job by ID
 */
export const getResearchJob = async (jobId: string): Promise<ResearchJob> => {
  const response = await enterpriseSystemsClient.get<ResearchJob>(
    `/research/jobs/${jobId}`
  );
  return response.data;
};

/**
 * Trigger new research for a system
 */
export const triggerSystemResearch = async (
  systemId: string,
  forceRefresh = false
): Promise<{ job_id: string; status: string; message: string }> => {
  // First get system details to create research request
  const systemData = await getEnterpriseSystem(systemId);
  const system = systemData.system;

  const response = await enterpriseSystemsClient.post<{
    job_id: string;
    status: string;
    message: string;
  }>('/research', {
    system_id: systemId,
    system_name: system.system_name,
    vendor: system.vendor,
    category: system.category,
    version: system.version,
    force_refresh: forceRefresh,
  });
  return response.data;
};

/**
 * Trigger research for specific modules
 */
export const triggerModuleResearch = async (
  systemId: string,
  moduleCodes: string[]
): Promise<{ job_id: string; status: string; message: string }> => {
  // First get system details to create research request
  const systemData = await getEnterpriseSystem(systemId);
  const system = systemData.system;

  const response = await enterpriseSystemsClient.post<{
    job_id: string;
    status: string;
    message: string;
  }>('/research', {
    system_id: systemId,
    system_name: system.system_name,
    vendor: system.vendor,
    category: system.category,
    version: system.version,
    module_codes: moduleCodes,  // Filter specific modules
    force_refresh: false,
  });
  return response.data;
};

/**
 * Cancel research job
 */
export const cancelResearchJob = async (
  jobId: string
): Promise<{ message: string }> => {
  const response = await enterpriseSystemsClient.delete<{ message: string }>(
    `/research/jobs/${jobId}`
  );
  return response.data;
};

// ============================================================================
// Autocomplete & Library Search
// ============================================================================

export interface LibrarySystem {
  system_name: string;
  vendor: string;
  category: string;
  version?: string;
  database_type?: string;
  aliases?: string[];
  search_score?: number;
}

export interface LibrarySearchResponse {
  results: LibrarySystem[];
  total: number;
  query: string;
}

export interface VendorSearchResponse {
  vendors: string[];
  total: number;
  query: string;
}

export interface VendorSuggestionResponse {
  system_name: string;
  suggested_vendor: string;
  confidence: 'high' | 'medium' | 'low';
  source: 'library_match' | 'llm_inference' | 'no_match';
  matched_system?: string;
  category?: string;
  version?: string;
  message?: string;
}

/**
 * Search enterprise systems library for autocomplete
 * Searches by name, vendor, or aliases (3+ characters)
 */
export const searchSystemsLibrary = async (
  query: string,
  limit = 10
): Promise<LibrarySearchResponse> => {
  const response = await enterpriseSystemsClient.get<LibrarySearchResponse>(
    '/library/search',
    {
      params: { q: query, limit },
    }
  );
  return response.data;
};

/**
 * Search vendor names for autocomplete (2+ characters)
 */
export const searchVendors = async (
  query: string,
  limit = 10
): Promise<VendorSearchResponse> => {
  const response = await enterpriseSystemsClient.get<VendorSearchResponse>(
    '/library/vendors',
    {
      params: { q: query, limit },
    }
  );
  return response.data;
};

/**
 * Get LLM-powered vendor suggestion based on system name
 */
export const suggestVendor = async (
  systemName: string
): Promise<VendorSuggestionResponse> => {
  const response = await enterpriseSystemsClient.post<VendorSuggestionResponse>(
    '/library/suggest-vendor',
    { system_name: systemName }
  );
  return response.data;
};

/**
 * Get all common systems from library (30 systems)
 */
export const getCommonSystems = async (): Promise<LibrarySystem[]> => {
  const response = await enterpriseSystemsClient.get<LibrarySystem[]>(
    '/meta/common-systems'
  );
  return response.data;
};

export default {
  // CRUD
  getEnterpriseSystems,
  getEnterpriseSystem,
  createEnterpriseSystem,
  updateEnterpriseSystem,
  deleteEnterpriseSystem,

  // Modules
  getSystemModules,
  getModule,
  getModuleStats,

  // Schema
  getSystemTables,
  getTable,

  // APIs
  getSystemAPIs,
  getAPI,

  // Integration & Documentation
  getIntegrationPatterns,
  getDocumentationLinks,

  // Quality
  triggerQualityAssessment,
  getLatestQualityAssessment,
  getQualityAssessmentHistory,

  // Statistics
  getSystemStatistics,

  // Research Jobs
  getResearchJobs,
  getResearchJob,
  triggerSystemResearch,
  cancelResearchJob,

  // Autocomplete & Library
  searchSystemsLibrary,
  searchVendors,
  suggestVendor,
  getCommonSystems,
};
